import { IEgeListItem } from '@ucheba/ui/components/FieldsSelectEge/types'
import { useDispatch, useSelector } from 'react-redux'
import { examsSelectors, examsThunks } from '@ucheba/store/exams'
import { useCallback, useEffect, useMemo } from 'react'
import { profileSelectors, profileThunks } from '@ucheba/store/profile'
import {
  personalAchievementsSelectors,
  personalAchievementsThunks,
} from '@ucheba/store/personalAchievements'
import { ELoading } from '@ucheba/store/utils/response/types'
import { coreSelectors } from '@ucheba/store/core'
import { extraExams } from '@ucheba/ui/components/FieldsAddEge/constants'
import { IEgeListField } from '@ucheba/ui/components/FieldsAddEge/types'
import { achievementsShort, excludeExamsIds, tooltipText } from './constants'
import { IAchievementInput } from '../../pages/for-abiturients/planner/interests/types'

interface IExams {
  examId: string[]
  score: string
}

export const useExams = (exams: IExams[]): IEgeListItem[] => {
  const dispatch = useDispatch()
  const examsOrigin = useSelector(examsSelectors.selectAll)

  useEffect(() => {
    if (!examsOrigin || examsOrigin.length === 0) {
      dispatch(examsThunks.fetch({}))
    }
  }, [dispatch])

  const ids = useMemo(() => {
    return exams.map((el) => el.examId && el.examId[0])
  }, [exams])

  return useMemo(
    () =>
      examsOrigin
        .map(({ id, subject }) => ({ id: String(id), name: subject.name }))
        .filter(({ id }) => !excludeExamsIds.includes(+id) || ids.includes(String(id))),
    [examsOrigin, ids]
  )
}

interface IInitialValues {
  egeList: {
    examId: string[]
    score: string
  }[]
  egeExtraExam: string[] | string
  achievements: string[] | []
}

interface IUseInitialValues {
  (): IInitialValues
}

export const useInitialValues: IUseInitialValues = () => {
  const profile = useSelector(profileSelectors.entity)

  const egeList = useMemo(() => {
    const checkedExams = profile?.enrolleeInfo?.exams

    if (checkedExams) {
      const list = checkedExams.map((exam) => ({
        examId: [String(exam.exam.id)],
        score: exam.score ? String(exam.score) : '',
      }))

      // если нет экзамена с ид 12 то добавляем - обязательный элемент
      if (!list.some((exam) => exam.examId.includes(String(12)))) {
        list.unshift({
          examId: ['12'],
          score: '',
        })

        return list
      }

      // переносим экзамен с ид 12 на первое место
      return list.reduce((arr, el) => {
        if (el.examId.includes('12')) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          arr.unshift(el)
        } else {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          arr.push(el)
        }

        return arr
      }, [])
    }

    return [
      {
        examId: ['12'],
        score: '',
      },
    ]
  }, [profile?.enrolleeInfo?.exams])

  const achievements = useMemo(() => {
    return profile?.enrolleeInfo?.achievements
      ? profile?.enrolleeInfo?.achievements.map((achieve) => String(achieve.id)) || []
      : []
  }, [profile?.enrolleeInfo?.achievements])

  return {
    egeList,
    egeExtraExam: [extraExams],
    achievements,
  }
}

export const useInputs = (): IAchievementInput[] => {
  const achievements = useSelector(personalAchievementsSelectors.selectAll)
  const dispatch = useDispatch()

  useEffect(() => {
    if (!achievements || achievements.length === 0) {
      dispatch(personalAchievementsThunks.fetch({}))
    }
  }, [])

  return useMemo(() => {
    return achievements.map((achievement) => {
      const isTooltipExist = tooltipText.filter(
        (tooltip) => tooltip.id === achievement.id
      )

      return isTooltipExist.length === 1
        ? {
            ...achievement,
            name: achievementsShort.find(({ id }) => id === achievement.id)?.text,
            tooltip: isTooltipExist[0].text,
            position: isTooltipExist[0].position,
          }
        : achievement
    }) as IAchievementInput[]
  }, [achievements])
}

interface IUseSelectEgeDialog {
  onSubmit: (value: any) => void
  loading: boolean
}

export const useSelectEgeDialog = (
  open: boolean,
  onAfterSubmit?: (value: any) => void,
  onBeforeSubmit?: (value: any) => void
): IUseSelectEgeDialog => {
  const dispatch = useDispatch()
  const loadingPlan = useSelector(personalAchievementsSelectors.loading)
  const loadingProfile = useSelector(profileSelectors.loading)
  const isTouch = useSelector(coreSelectors.isTouch)

  useEffect(() => {
    if (isTouch && open) {
      document.body.style.overflow = 'hidden'
    } else if (isTouch) {
      document.body.style.overflow = 'auto'
    }
  }, [isTouch, open])

  const loading = useMemo(() => {
    if (loadingPlan === ELoading.pending || loadingProfile === ELoading.pending) {
      return true
    }

    return false
  }, [loadingProfile, loadingPlan])

  const convertExams: any = (exams) => {
    return exams
      .filter(({ examId }) => examId)
      .map(({ examId, score }) => ({
        examId: Number(examId[0]),
        score: score ? Number(score) : null,
      }))
  }

  const onSubmit = useCallback(
    async (value: IInitialValues) => {
      if (onBeforeSubmit) {
        onBeforeSubmit(value)
      }

      await dispatch(
        profileThunks.patch({
          data: {
            enrolleeInfo: {
              exams: convertExams(value.egeList),
              achievementIds: value.achievements,
              readyForAdditionalExam: value.egeExtraExam.includes(extraExams),
            },
          },
        })
      )

      await dispatch(profileThunks.get({}))

      if (onAfterSubmit) {
        onAfterSubmit(value)
      }
    },
    [dispatch, onAfterSubmit, onBeforeSubmit]
  )

  return {
    onSubmit,
    loading,
  }
}

interface IUseFieldAddEge {
  (
    list: IEgeListItem[],
    values: IEgeListField[]
  ): {
    getExams: (index: number) => IEgeListItem[]
  }
}

export const useFieldAddEge: IUseFieldAddEge = (list, values) => {
  const selectedMap = useMemo(() => {
    return values.reduce((map, value) => {
      if (value.examId && value.examId.length) {
        map.add(value.examId[0])
      }
      return map
    }, new Set())
  }, [values])

  const getExams = useCallback(
    (index) => {
      return list.filter(
        (listItem) =>
          !selectedMap.has(listItem.id) ||
          (values[index].examId &&
            values[index].examId.length &&
            values[index].examId[0] === listItem.id)
      )
    },
    [list, values, selectedMap]
  )

  return {
    getExams,
  }
}
